/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "OneTrust Snowflake Native App", description = "Bring OneTrust consent data into Hightouch through the OneTrust Snowflake Native App, then enforce it with subsets, destination rules, and sync behavior.", lastUpdatedDate = new Date(1788825600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "OneTrust Snowflake Native App",
        description: "Bring OneTrust consent data into Hightouch through the OneTrust Snowflake Native App, then enforce it with subsets, destination rules, and sync behavior.",
        lastUpdatedDate: "Tue Sep 08 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Connect OneTrust consent data",
          "depth": 0,
          "id": "connect-onetrust-consent-data"
        }, {
          "value": "Enforce consent in Hightouch",
          "depth": 0,
          "id": "enforce-consent-in-hightouch"
        }, {
          "value": "Filter audiences by consent with subsets",
          "depth": 1,
          "id": "filter-audiences-by-consent-with-subsets"
        }, {
          "value": "Suppress unconsented records with destination rules",
          "depth": 1,
          "id": "suppress-unconsented-records-with-destination-rules"
        }, {
          "value": "Remove records when consent is revoked",
          "depth": 1,
          "id": "remove-records-when-consent-is-revoked"
        }, {
          "value": "Model consent and preference data",
          "depth": 0,
          "id": "model-consent-and-preference-data"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      img: "img",
      ol: "ol",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/overview#for-data-teams-and-platform-admins",
                children: "Set up for platform admins and data teams"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "The OneTrust Snowflake Native App streams consent and preference data from OneTrust into your Snowflake warehouse. Once it's connected to your schema, Hightouch can enforce that consent with subsets, destination rules, and sync behavior."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["If you use OneTrust ", _jsx(_components.a, {
          href: "https://www.onetrust.com/products/universal-consent-and-preference-management/",
          children: "Universal Consent and Preference Management"
        }), " (UCPM) to manage customer consent and communication preferences, the ", _jsx(_components.a, {
          href: "https://www.onetrust.com/news/onetrust-accelerates-access-to-trusted-customer-data-with-snowflake-native-app-for-consent-management/",
          children: "OneTrust Snowflake Native App"
        }), " syncs that data into your Snowflake warehouse in near real time. Every opt-in, opt-out, and preference change stays current, and Snowflake's own governance controls mask rows and columns so each role only sees what it's permissioned for."]
      }), "\n", _jsx(_components.p, {
        children: "Once consent lives in your warehouse, Hightouch can reference it to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Filter audiences with ", _jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: "subsets"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Block unconsented records with ", _jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "destination rules"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Remove records from destinations when a customer revokes consent."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Together, these ensure destinations only receive data from customers who have given the appropriate consent."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/onetrust/onetrust-diagram.png",
          alt: "Diagram of the OneTrust Snowflake Native App workflow"
        })
      }), "\n", _jsx(_components.h2, {
        id: "connect-onetrust-consent-data",
        children: "Connect OneTrust consent data"
      }), "\n", _jsx(_components.p, {
        children: "Bring OneTrust data into Hightouch by connecting it as a source and adding it to your schema."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Install the OneTrust Snowflake Native App."
            }), " Follow ", _jsx(_components.a, {
              href: "https://my.onetrust.com/s/article/UUID-2f18f280-ec76-c4c5-2419-73615c2ad4dd?language=en_US&topicId=0TO1Q000000ItSRWA0",
              children: "OneTrust's setup guide"
            }), " to stream consent and preference updates into a table in your Snowflake warehouse."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Connect Snowflake as a source."
            }), " Follow the ", _jsx(_components.a, {
              href: "/sources/snowflake",
              children: "Snowflake source setup"
            }), " so Hightouch can read the OneTrust data."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Create a parent model."
            }), " Go to ", _jsx(_components.strong, {
              children: "Customer Studio > Schema"
            }), ", click ", _jsx(_components.strong, {
              children: "Create parent model"
            }), ", and select your primary user table (for example, ", _jsx(_components.code, {
              children: "users"
            }), "). See ", _jsx(_components.a, {
              href: "/customer-studio/schema#create-the-parent-model",
              children: "Define your schema"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsxs(_components.strong, {
              children: ["Add your consent table as a ", _jsx(_components.a, {
                href: "/customer-studio/schema#add-a-related-model",
                children: "related model"
              }), "."]
            }), " Join it to the parent model and choose a relationship type:"]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["Use ", _jsx(_components.strong, {
                children: "1:1"
              }), " if you have a single consent purpose."]
            }), "\n", _jsxs(_components.li, {
              children: ["Use ", _jsx(_components.strong, {
                children: "1:many"
              }), " if you have multiple consent purposes, since OneTrust stores each type in a separate row."]
            }), "\n"]
          }), "\n", _jsx(_components.p, {
            children: "You may want to transform the consent table or expose a view first, depending on your OneTrust setup."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Select the consent columns to expose"
            }), ", such as ", _jsx(_components.code, {
              children: "email_opt_in"
            }), ", ", _jsx(_components.code, {
              children: "sms_opt_in"
            }), ", or ", _jsx(_components.code, {
              children: "ad_sharing_opt_in"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "These fields are then available throughout Hightouch to filter audiences, enforce destination rules, and drive sync behavior based on consent."
      }), "\n", _jsx(_components.h2, {
        id: "enforce-consent-in-hightouch",
        children: "Enforce consent in Hightouch"
      }), "\n", _jsx(_components.h3, {
        id: "filter-audiences-by-consent-with-subsets",
        children: "Filter audiences by consent with subsets"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "subsets"
        }), " to define reusable, consent-based filters. Because subsets apply as base filters across audiences and syncs, they keep unconsented customers out of every audience built on that parent model."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides a built-in ", _jsx(_components.strong, {
          children: "Consent management"
        }), " subset category with default consent subsets, so you can grant access to a subset like ", _jsx(_components.strong, {
          children: "Email"
        }), " without building it from scratch. You can also create your own. For example, define a subset that includes only records where ", _jsx(_components.code, {
          children: "email_opt_in = true"
        }), ". Once created, a subset can be:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Applied to multiple audiences for consistent filtering across use cases."
        }), "\n", _jsx(_components.li, {
          children: "Set as required for a team, so those marketers can't build audiences without selecting a consent value."
        }), "\n", _jsx(_components.li, {
          children: "Restricted to approved values, so users can only select the options you allow."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/onetrust/subset-email-opt-in.png",
          alt: "Subset definition filtering on a consent field"
        })
      }), "\n", _jsx(_components.h3, {
        id: "suppress-unconsented-records-with-destination-rules",
        children: "Suppress unconsented records with destination rules"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/customer-studio/destination-rules",
          children: "destination rules"
        }), " to block records from syncing to a destination when they don't meet consent criteria. For example, block records where ", _jsx(_components.code, {
          children: "sms_opt_in != true"
        }), " from syncing to your SMS tool. This acts as a final safeguard against delivering unconsented data."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/onetrust/rule-definition.png",
          alt: "Example rule"
        })
      }), "\n", _jsx(_components.h3, {
        id: "remove-records-when-consent-is-revoked",
        children: "Remove records when consent is revoked"
      }), "\n", _jsxs(_components.p, {
        children: ["When a customer revokes consent, Hightouch can remove them from downstream tools — but only if the model is filtered by consent ", _jsx(_components.strong, {
          children: "and"
        }), " the sync is configured to support removals."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Recurring syncs."
        }), " On the next scheduled run, Hightouch removes any records that no longer match the model's filter. This requires:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A consent-based filter on the model or audience (for example, ", _jsx(_components.code, {
            children: "email_opt_in = TRUE"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["A ", _jsx(_components.a, {
            href: "/syncs/types-and-modes#sync-modes",
            children: "sync mode"
          }), " that supports removals."]
        }), "\n", _jsx(_components.li, {
          children: "Record removal enabled in the sync configuration."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/onetrust/removal-options.png",
          alt: "Removal options"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Triggered syncs."
        }), " If your model and destination support real-time updates (for example, through change data capture or an event stream), Hightouch can configure the sync as triggered. Enable record removal on the sync so a customer is dropped from the destination as soon as their consent changes, rather than on the next scheduled run."]
      }), "\n", _jsx(_components.h2, {
        id: "model-consent-and-preference-data",
        children: "Model consent and preference data"
      }), "\n", _jsxs(_components.p, {
        children: ["The OneTrust Snowflake Native App can sync far more than binary opt-ins. When you ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "define your schema"
        }), ", consider exposing:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Channel-specific consent"
          }), " — ", _jsx(_components.code, {
            children: "email_opt_in"
          }), ", ", _jsx(_components.code, {
            children: "sms_opt_in"
          }), ", ", _jsx(_components.code, {
            children: "push_opt_in"
          }), ", so you can enforce channel-level rules in subsets and destination rules."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Frequency preferences"
          }), " — for example, ", _jsx(_components.code, {
            children: "email_frequency = weekly"
          }), ", to suppress over-messaging by filtering for lower-frequency segments."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Content or category preferences"
          }), " — for example, ", _jsx(_components.code, {
            children: "category_preference = mens_clothing"
          }), ", to target customers by stated interest."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data-sharing consent"
          }), " — for example, ", _jsx(_components.code, {
            children: "ad_sharing_platform_opt_in = true"
          }), ", so records only reach third-party platforms like Meta or Google when consent exists."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: "Subsets"
          }), " to scope audiences by consent."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "Destination rules"
          }), " to block unconsented records at the destination."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/events/consent/consent-manager",
            children: "Consent Manager for Events"
          }), " and ", _jsx(_components.a, {
            href: "/events/consent/onetrust",
            children: "OneTrust for Events"
          }), " for event-based consent collection."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://my.onetrust.com/s/article/UUID-9f6e9cab-3016-5dd0-342f-7f03287c358d?language=en_US",
            children: "OneTrust Universal Consent and Preferences Management"
          }), " for OneTrust's own documentation."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
